/**
 * @module manifest/defineGroup
 * @description 配置项组声明式注册：单一定义派生 groupRegistry / upload-keys
 *
 * 新增配置项目标形态：写 1 个 `defineGroup({...})` + 在 panelLayout 引用 id；
 * 避免在 groupRegistry、upload-keys 等多处手写重复登记。
 */

import type { ComponentType } from 'react';
import type { TChartType } from '@dbl/core/chart';
import type { VisibilityFn } from './visibility/visibilityFactories';

/** 某图表类型下该配置组的显隐（组级 + 子项级） */
export interface IGroupChartVisibility {
  visible?: VisibilityFn;
  itemsVisibility?: Readonly<Record<string, VisibilityFn>>;
}

/** 兼容 function 组件与 React.memo / forwardRef（非 function 引用） */
function isReactComponentType(component: unknown): boolean {
  if (typeof component === 'function') {
    return true;
  }
  if (typeof component === 'object' && component !== null) {
    return typeof (component as { $$typeof?: unknown }).$$typeof === 'symbol';
  }
  return false;
}

/** 分类组件统一 Props（与 GroupComponentWrapper 透传一致） */
export interface IChartSettingsGroupComponentProps {
  visibleItems?: Record<string, boolean>;
}

/** 组内子项（无独立 groupRegistry 条目，仅用于 formDelKey 子项级过滤） */
export interface IGroupItemDefinition {
  readonly id: string;
  readonly uploadKeys: readonly string[];
  /**
   * Confirm 后是否触发后端重算；默认 `true`。
   * `false` 时该子项 uploadKeys 写入 `NOT_CALCULATE_KEYS`（PRD §七 notCalculateKey）。
   */
  readonly recalcOnConfirm?: boolean;
}

/**
 * 单个配置项组的完整声明
 *
 * - `id`：与 panelLayout 组 id、groupRegistry 键名一致
 * - `uploadKeys`：整组隐藏时 convert 收集的字段并集（通常 = items 字段合并）
 * - `items`：组内子项 → 字段；与 chartVisibility.itemsVisibility 的 key 对齐
 * - `chartVisibility`：按图表类型覆盖显隐；panelLayout 只列组 id，运行时由 layoutToResolvedGroups 解析
 * - `recalcOnConfirm`：无 `items` 时作用于整组 uploadKeys；有 `items` 时仅作用于未被子项覆盖的字段
 */
export interface IGroupDefinition {
  readonly id: string;
  readonly component: ComponentType<IChartSettingsGroupComponentProps>;
  readonly uploadKeys: readonly string[];
  /** 整组或散落字段的 Confirm 重算策略；默认 `true` */
  readonly recalcOnConfirm?: boolean;
  readonly items?: readonly IGroupItemDefinition[];
  readonly chartVisibility?: Partial<
    Record<TChartType, IGroupChartVisibility>
  >;
}

/**
 * 声明工厂：集中校验必填字段，返回原对象供类型收窄
 */
export function defineGroup<T extends IGroupDefinition>(definition: T): T {
  const { id, component, uploadKeys } = definition;

  if (!id || typeof id !== 'string') {
    throw new Error('[defineGroup] id 必须为非空字符串');
  }
  if (!isReactComponentType(component)) {
    throw new Error(`[defineGroup] ${id}: component 必须为 React 组件`);
  }
  if (!Array.isArray(uploadKeys) || uploadKeys.length === 0) {
    throw new Error(`[defineGroup] ${id}: uploadKeys 至少包含一个上传字段`);
  }

  return definition;
}

/** 由声明列表派生 id → 组件注册表 */
export function definitionsToGroupRegistry(
  definitions: readonly IGroupDefinition[],
): Record<string, ComponentType<IChartSettingsGroupComponentProps>> {
  const registry: Record<
    string,
    ComponentType<IChartSettingsGroupComponentProps>
  > = {};

  definitions.forEach(def => {
    if (registry[def.id]) {
      throw new Error(
        `[defineGroup] 重复的 group id: ${def.id}`,
      );
    }
    registry[def.id] = def.component;
  });

  return registry;
}

/** 由声明列表派生 id → 上传字段名（可变数组副本，供 convert 合并修改） */
export function definitionsToUploadKeys(
  definitions: readonly IGroupDefinition[],
): Record<string, string[]> {
  const map: Record<string, string[]> = {};

  definitions.forEach(def => {
    if (map[def.id]) {
      throw new Error(
        `[defineGroup] 重复的 upload-keys id: ${def.id}`,
      );
    }
    map[def.id] = [...def.uploadKeys];
  });

  return map;
}

/** 声明 id 集合（用于与 legacy 注册对账） */
export function getDefinitionIds(
  definitions: readonly IGroupDefinition[],
): string[] {
  return definitions.map(d => d.id);
}

/**
 * 由声明列表派生「组 id → 子项 id → 上传字段」
 *
 * 供 evaluateVisibility 在子项隐藏时计算 formDelKey；勿在 upload-keys 再平铺子项 id。
 */
export function definitionsToGroupItemUploadKeys(
  definitions: readonly IGroupDefinition[],
): Record<string, Record<string, string[]>> {
  const map: Record<string, Record<string, string[]>> = {};

  definitions.forEach(def => {
    if (!def.items?.length) {
      return;
    }
    const itemMap: Record<string, string[]> = {};
    def.items.forEach(item => {
      if (itemMap[item.id]) {
        throw new Error(
          `[defineGroup] ${def.id} 重复的子项 id: ${item.id}`,
        );
      }
      itemMap[item.id] = [...item.uploadKeys];
    });
    map[def.id] = itemMap;
  });

  return map;
}

function resolveUploadKeyRecalcOnConfirm(
  def: IGroupDefinition,
  uploadKey: string,
): boolean {
  if (def.items?.length) {
    for (const item of def.items) {
      if (item.uploadKeys.includes(uploadKey)) {
        return item.recalcOnConfirm !== false;
      }
    }
  }
  return def.recalcOnConfirm !== false;
}

/**
 * 由声明列表派生 PRD §七 notCalculateKey 集合（Confirm 仅前端刷新、不 BUMP 重算）
 *
 * @param extraKeys - 非 defineGroup 管辖字段（如历史只读兼容键）
 */
export function definitionsToNotCalculateKeys(
  definitions: readonly IGroupDefinition[],
  extraKeys: readonly string[] = [],
): ReadonlySet<string> {
  const keys = new Set<string>(extraKeys);

  definitions.forEach(def => {
    def.uploadKeys.forEach(uploadKey => {
      if (!resolveUploadKeyRecalcOnConfirm(def, uploadKey)) {
        keys.add(uploadKey);
      }
    });
  });

  return keys;
}
